import { useCustomers } from '@src/hooks/useCustomer';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useCustomerMarketAccounts } from '@src/providers';
import { ACTION, EMPTY_ARRAY, EntityAdmin } from '@talos/kyoko';
import { useMemo } from 'react';
import { DEFAULT_CUSTOMER_CREDITS_CURRENCIES } from '../CustomerCredits/utils';
import { getCustomerMarginConfigurationDrawerEntity, getCustomerMarginConfigurationEntityForSave } from './mappers';
import {
  CUSTOMER_MARGIN_CONFIGURATION_PATH,
  useCustomerMarginConfigurationsQuery,
  useFundingSchedulesQuery,
} from './queries';
import type { CustomerMarginConfiguration, CustomerMarginConfigurationDrawerEntity } from './types';
import { CUSTOMER_MARGIN_CONFIGURATIONS_COLUMNS, getCustomerAccountConfigurationDrawerOptions } from './utils';

export function CustomerMarginConfiguration() {
  const { isAuthorized } = useRoleAuth();
  const customers = useCustomers();
  const { customerMarketAccountsList } = useCustomerMarketAccounts();
  const canManageConfigurations = isAuthorized(ACTION.EDIT_DEALER) || isAuthorized(ACTION.EDIT_DEALER_TRADING);
  const { data: fundingSchedules } = useFundingSchedulesQuery({ enabled: canManageConfigurations });

  // need this prefetch to get already configured accounts for the drawer dropdown
  const { data: customerMarginConfigurations, isLoading: isLoadingCustomerMarginConfigurations } =
    useCustomerMarginConfigurationsQuery({ enabled: canManageConfigurations });

  const configuredMarketAccountNames = useMemo(
    // Track already configured accounts so create mode can exclude them to avoid PATCH overwrites.
    () => new Set((customerMarginConfigurations ?? EMPTY_ARRAY).map(({ MarketAccount }) => MarketAccount)),
    [customerMarginConfigurations]
  );

  const drawerOptions = useMemo(() => {
    if (!customerMarketAccountsList || !customers || isLoadingCustomerMarginConfigurations || !fundingSchedules) {
      return undefined;
    }
    return getCustomerAccountConfigurationDrawerOptions({
      customerMarketAccountsList,
      configuredMarketAccountNames,
      customers,
      currencies: DEFAULT_CUSTOMER_CREDITS_CURRENCIES,
      schedules: fundingSchedules,
    });
  }, [
    configuredMarketAccountNames,
    customerMarketAccountsList,
    customers,
    fundingSchedules,
    isLoadingCustomerMarginConfigurations,
  ]);

  return (
    <EntityAdmin<CustomerMarginConfiguration, CustomerMarginConfigurationDrawerEntity>
      title="Customer Margin Configurations"
      entityName="Customer Margin Configuration"
      path={CUSTOMER_MARGIN_CONFIGURATION_PATH}
      entityIDField="MarketAccount"
      // DELETE intentionally omitted, no support on endpoint
      allowAddEntity={canManageConfigurations}
      allowEditEntity={canManageConfigurations}
      columns={CUSTOMER_MARGIN_CONFIGURATIONS_COLUMNS}
      persistKey="dealer/customer-margin-configuration"
      filterableProperties={EMPTY_ARRAY}
      drawerOptions={drawerOptions}
      newEntityDefault={() => ({
        MarginTrading: 'enabled',
      })}
      getEntityAsDrawerEntity={getCustomerMarginConfigurationDrawerEntity}
      getEntityForPatchPost={getCustomerMarginConfigurationEntityForSave}
      removeNullValuesFromPatchPayload={true}
      // Override EntityAdmin's default `/{entityIDField}` PATCH path, since this endpoint only works on the base path.
      getPatchPath={() => CUSTOMER_MARGIN_CONFIGURATION_PATH}
      // no post exists for this endpoint
      shouldUsePatchForPost={() => true}
    />
  );
}
